import {
    compact,
    map,
    pipe,
    filter,
    groupBy,
    toPairs,
    keys,
    flatten,
    some,
    find,
    isEmpty,
    isNil
} from 'lodash/fp';
import {
    SQL_VIEW_DTO_TYPE,
    DATA_SOURCE_REFERENCE_DTO_TYPE,
    TRANSFORMATION_STACK_DTO_TYPE
} from 'app/store/__generated__/types';
import { resolveIconForType } from 'app/lib/utils/resolveIconForType';
import { quoteIfNeeded } from 'app/lib/utils/sql';

const BOOSTS = {
    [SQL_VIEW_DTO_TYPE]: 95,
    [TRANSFORMATION_STACK_DTO_TYPE]: 95,
    [DATA_SOURCE_REFERENCE_DTO_TYPE]: 90,
    DATABASE: 85,
    SCHEMA: 85,
    TABLE: 85,
    VIEW: 85,
    COLUMN: 85
};

const ICONS = {
    [SQL_VIEW_DTO_TYPE]: 'op-sql',
    [DATA_SOURCE_REFERENCE_DTO_TYPE]: 'table'
};

export const databases = (catalog) =>
    pipe(
        groupBy('database'),
        toPairs,
        map(([k, v]) => ({ label: k, schemaCount: keys(groupBy('schema', v)).length }))
    )(catalog);

export const schemas = (catalog, database) =>
    pipe(
        filter((sl) => sl.database === database),
        groupBy('schema'),
        toPairs,
        map(([k, v]) => ({ label: k, tableCount: v.length }))
    )(catalog);

export const tables = (catalog, database, schema) =>
    pipe(
        filter((sl) => sl.database === database && sl.schema === schema),
        map((sl) => ({ label: sl.table, sourceType: sl.sourceType }))
    )(catalog);

export function completeDatabases(catalog) {
    const list = databases(catalog);

    return list.map(({ label, schemaCount }) => ({
        label,
        apply: quoteIfNeeded(label),
        icon: 'database',
        boost: BOOSTS.DATABASE,
        type: 'database',
        detail: `${schemaCount} ${schemaCount === 1 ? 'schema' : 'schemas'}`
    }));
}

function getProjectNodeInfo(type) {
    return type === DATA_SOURCE_REFERENCE_DTO_TYPE ? 'datasource' : 'transformation node';
}

export function completeProjectNodes(assets) {
    const list = map((a) => ({ label: a.name, type: a.type }), assets);
    return list.map(({ label, type }) => {
        const icon = ICONS[type] || resolveIconForType(type);

        return {
            label,
            apply: quoteIfNeeded(label),
            icon,
            boost: BOOSTS[type],
            type: getProjectNodeInfo(type)
        };
    });
}

export function completeSchemas(identifiers, catalog) {
    const list = schemas(catalog, identifiers[1]);

    return list.map(({ label, tableCount }) => ({
        label,
        apply: quoteIfNeeded(label),
        icon: 'schema',
        boost: BOOSTS.SCHEMA,
        type: 'schema',
        detail: `${tableCount} ${tableCount === 1 ? 'table or view' : 'tables and views'}`
    }));
}

export function completeTables(identifiers, catalog) {
    const list = tables(catalog, identifiers[2], identifiers[1]);

    return list.map(({ label, sourceType }) => ({
        label,
        apply: quoteIfNeeded(label),
        icon: resolveIconForType(sourceType),
        boost: BOOSTS[sourceType],
        type: sourceType.toLowerCase(),
        detail: ' '
    }));
}

export function completeSources(catalog, assets) {
    return (identifiers) => {
        if (identifiers.length === 1) {
            return [...completeProjectNodes(assets), ...completeDatabases(catalog)];
        }

        if (identifiers.length === 2) {
            return completeSchemas(identifiers, catalog);
        }

        if (identifiers.length === 3) {
            return completeTables(identifiers, catalog);
        }

        return [];
    };
}

export function completeColumns(assets) {
    return (identifiers, tableNames) => {
        let filteredAssets = [];

        if (identifiers.length === 1) {
            filteredAssets = assets.filter((a) =>
                some((t) => a.name.toLowerCase() === t.name.toLowerCase(), tableNames)
            );
        } else if (identifiers.length === 2) {
            const tableName = find((t) => t.alias === identifiers[1], tableNames);
            const asset = assets.find(
                (a) => a.name.toLowerCase() === (tableName?.name || identifiers[1]).toLowerCase()
            );

            if (isNil(asset)) {
                return [];
            }

            filteredAssets = [asset];
        }

        const aliasesFor = (name) =>
            pipe(
                filter((t) => t.name === name),
                map('alias'),
                compact
            )(tableNames);

        const columns = flatten(
            filteredAssets.map((a) => {
                const aliases = aliasesFor(a.name);
                const hasAliases = !isEmpty(aliases);

                if (!hasAliases) {
                    aliases.push(a.name);
                }

                const columns = [];

                for (const name of aliases) {
                    for (const c of a.schema) {
                        columns.push({
                            name: c.name,
                            type: c.type,
                            table: name,
                            isAlias: hasAliases,
                            unique: true
                        });
                    }
                }

                return columns;
            })
        );

        for (const column of columns) {
            const other = find((c) => c.name === column.name && c.table !== column.table, columns);

            if (other) {
                column.unique = false;
            }
        }

        return map(
            (c) => ({
                label: c.unique ? c.name : `${c.table}.${c.name}`,
                apply: c.unique
                    ? quoteIfNeeded(c.name)
                    : `${c.isAlias ? c.table : quoteIfNeeded(c.table)}.${quoteIfNeeded(c.name)}`,
                icon: resolveIconForType(c.type),
                boost: BOOSTS.COLUMN,
                type: 'column',
                detail: c.type
            }),
            columns
        );
    };
}

export function completeColumnsForFormula(columns) {
    return () =>
        map(
            (c) => ({
                label: c.name,
                apply: quoteIfNeeded(c.name),
                icon: resolveIconForType(c.type),
                boost: BOOSTS.COLUMN,
                type: 'column',
                detail: c.type
            }),
            columns
        );
}
